import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import {
  EmailChipInput,
  splitEmailTokens,
  type EmailChipSuggestion,
} from "@/components/ui/EmailChipInput";
import {
  HtmlMailBodyEditor,
  type HtmlMailBodyInsertHandle,
} from "@/components/ui/HtmlMailBodyEditor";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import {
  composeDokumentMail,
  sendDokumentMail,
  type DokumentMailPrimaryAttachment,
  type DokumentMailRelatedDocument,
  type DokumentMailSuggestedTimesheet,
  type DokumentMailSuggestedLieferschein,
} from "@/lib/dokumente";
import {
  encodeLocalMailAttachments,
  formatAttachmentSize,
  lieferscheinMailVariantLabel,
  localMailAttachmentsFromFileList,
  preferSignedProofSharepointItems,
  type LocalMailAttachment,
} from "@/lib/mailAttachments";
import { mailSharepointSuggestions, type SharePointMailItem } from "@/lib/orders";
import { SharePointMailAttachmentList } from "@/components/SharePointMailAttachmentList";
import "@/pages/dokumente/dokumente.css";

interface DocumentMailDialogProps {
  open: boolean;
  docId: number;
  onClose: () => void;
  onSent: () => void | Promise<void>;
  onError?: (message: string) => void;
  zIndexClass?: string;
  /** Optional: E-Mail-Vorlage, z. B. ``angebot_nachfass``. */
  vorlage?: string;
  /** Standard für „Als Versendet markieren“ (Default: true). */
  defaultMarkVersendet?: boolean;
}

function emailsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v.toLowerCase() === b[i]?.toLowerCase());
}

function idSetsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function joinEmails(list: string[]): string {
  return list.join("; ");
}

type SuggestedProofDoc = DokumentMailSuggestedTimesheet | DokumentMailSuggestedLieferschein;

function canPreselectProof(doc: SuggestedProofDoc): boolean {
  return doc.has_pdf !== false && doc.has_signed_pdf !== false;
}

function proofAttachmentHint(doc: SuggestedProofDoc): string {
  if (doc.dokument_typ === "lieferschein") {
    if (doc.has_pdf === false || doc.has_signed_pdf === false) return "Kein unterschriebenes PDF";
    return lieferscheinMailVariantLabel(true);
  }
  if (doc.has_pdf === false) return "Kein PDF vorhanden";
  if (doc.has_signed_pdf === false) return "Kein unterschriebenes PDF";
  return doc.filename || "Unterschriebenes PDF mitanhängen";
}

export function DocumentMailDialog({
  open,
  docId,
  onClose,
  onSent,
  onError,
  zIndexClass,
  vorlage,
  defaultMarkVersendet = true,
}: DocumentMailDialogProps) {
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [suggestions, setSuggestions] = useState<EmailChipSuggestion[]>([]);
  const [primaryAttachment, setPrimaryAttachment] = useState<DokumentMailPrimaryAttachment | null>(
    null,
  );
  const [versandFormat, setVersandFormat] = useState<string | null>(null);
  const [relatedDocuments, setRelatedDocuments] = useState<DokumentMailRelatedDocument[]>([]);
  const [suggestedTimesheets, setSuggestedTimesheets] = useState<DokumentMailSuggestedTimesheet[]>(
    [],
  );
  const [suggestedLieferscheine, setSuggestedLieferscheine] = useState<
    DokumentMailSuggestedLieferschein[]
  >([]);
  const [selectedRelatedIds, setSelectedRelatedIds] = useState<Set<number>>(new Set());
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<Set<number>>(new Set());
  const [baselineTimesheetIds, setBaselineTimesheetIds] = useState<Set<number>>(new Set());
  const [baselineRelatedIds, setBaselineRelatedIds] = useState<Set<number>>(new Set());
  const [auftragId, setAuftragId] = useState<number | null>(null);
  const [spItems, setSpItems] = useState<SharePointMailItem[]>([]);
  const [spHint, setSpHint] = useState("");
  const [spLoading, setSpLoading] = useState(false);
  const [selectedSp, setSelectedSp] = useState<Set<string>>(new Set());
  const [localAttachments, setLocalAttachments] = useState<LocalMailAttachment[]>([]);
  const [localDropActive, setLocalDropActive] = useState(false);
  const [markVersendet, setMarkVersendet] = useState(defaultMarkVersendet);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [baseline, setBaseline] = useState({
    to: [] as string[],
    cc: [] as string[],
    bcc: [] as string[],
    subject: "",
    bodyHtml: "",
  });
  const [bodyEdited, setBodyEdited] = useState(false);
  const editorRef = useRef<HtmlMailBodyInsertHandle>(null);
  const localFileInputRef = useRef<HTMLInputElement | null>(null);
  const localDropCounterRef = useRef(0);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  onCloseRef.current = onClose;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!open) {
      setGlobalUploadSuppressed(false);
      return;
    }
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, [open]);

  // Nur open/docId/vorlage – nicht onClose/onError: Parent-Rerenders (Realtime/Autosave)
  // würden sonst die Compose-Vorschau neu laden und Eingaben verwerfen.
  useEffect(() => {
    if (!open || !docId) return;
    let cancelled = false;
    setLoading(true);
    setSelectedRelatedIds(new Set());
    setSelectedTimesheetIds(new Set());
    setBaselineTimesheetIds(new Set());
    setBaselineRelatedIds(new Set());
    setSuggestedTimesheets([]);
    setSuggestedLieferscheine([]);
    setSelectedSp(new Set());
    setLocalAttachments([]);
    setLocalDropActive(false);
    setMarkVersendet(defaultMarkVersendet);
    localDropCounterRef.current = 0;
    void composeDokumentMail(docId, vorlage ? { vorlage } : undefined)
      .then((data) => {
        if (cancelled) return;
        const html = data.html_body || "";
        const toList = splitEmailTokens(data.to || "");
        const ccList = splitEmailTokens(data.cc || "");
        setTo(toList);
        setCc(ccList);
        setBcc([]);
        setSubject(data.subject || "");
        setBodyHtml(html);
        setSuggestions(
          Array.isArray(data.recipient_suggestions)
            ? data.recipient_suggestions.map((s) => ({
                email: s.email,
                label: s.label,
                source: s.source,
              }))
            : [],
        );
        setBaseline({
          to: toList,
          cc: ccList,
          bcc: [],
          subject: data.subject || "",
          bodyHtml: html,
        });
        setPrimaryAttachment(
          data.primary_attachment || {
            filename: data.attachment_hint || "Dokument.pdf",
            content_type: "application/pdf",
            label: data.attachment_hint || "PDF",
          },
        );
        const format = data.versand_rechnungsformat || null;
        setVersandFormat(format);
        const timesheets = Array.isArray(data.suggested_timesheets)
          ? data.suggested_timesheets
          : [];
        const lieferscheine = Array.isArray(data.suggested_lieferscheine)
          ? data.suggested_lieferscheine
          : [];
        setSuggestedTimesheets(timesheets);
        setSuggestedLieferscheine(lieferscheine);
        // Bei XRechnung bereits eingebettet – nicht zusätzlich vorwählen.
        // Bei ZUGFeRD/PDF die unterschriebene Quell-PDF vorwählen, sofern der Kunde das nicht abgewählt hat.
        const preselectProofs =
          format !== "xrechnung" && data.stundennachweis_mit_rechnung !== false;
        const preselectedTs = preselectProofs
          ? new Set(timesheets.filter(canPreselectProof).map((ts) => ts.id))
          : new Set<number>();
        const preselectedLs = preselectProofs
          ? new Set(lieferscheine.filter(canPreselectProof).map((ls) => ls.id))
          : new Set<number>();
        setSelectedTimesheetIds(preselectedTs);
        setBaselineTimesheetIds(preselectedTs);
        setSelectedRelatedIds(preselectedLs);
        setBaselineRelatedIds(preselectedLs);
        setRelatedDocuments(Array.isArray(data.related_documents) ? data.related_documents : []);
        setAuftragId(
          data.auftrag_id != null && Number(data.auftrag_id) > 0 ? Number(data.auftrag_id) : null,
        );
        setBodyEdited(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        onErrorRef.current?.(
          err instanceof Error ? err.message : "Mail-Vorschau konnte nicht geladen werden.",
        );
        onCloseRef.current();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, docId, vorlage, defaultMarkVersendet]);

  const loadSharepointItems = useCallback(async () => {
    if (!auftragId) {
      setSpItems([]);
      setSpHint("");
      return;
    }
    setSpLoading(true);
    setSpHint("Lade Projektdateien …");
    try {
      const payload = await mailSharepointSuggestions({ auftragIds: [auftragId] });
      const items = preferSignedProofSharepointItems(
        Array.isArray(payload?.items) ? payload.items : [],
      );
      setSpItems(items);
      const nProj = payload?.sharepoint_projects ?? 0;
      if (!nProj) setSpHint("Kein SharePoint-Projektordner zu diesem Auftrag gefunden.");
      else if (!items.length) setSpHint("Im Projektordner wurden keine Dateien gefunden.");
      else setSpHint(`${items.length} Datei(en) — zum Mitanhängen auswählen.`);
    } catch {
      setSpHint("Dateivorschläge konnten nicht geladen werden.");
    } finally {
      setSpLoading(false);
    }
  }, [auftragId]);

  useEffect(() => {
    if (!open || loading) {
      setSpItems([]);
      setSpHint("");
      setSpLoading(false);
      return;
    }
    void loadSharepointItems();
  }, [open, loading, loadSharepointItems]);

  function addLocalFiles(fileList: FileList | File[]) {
    const next = localMailAttachmentsFromFileList(fileList);
    if (!next.length) return;
    setLocalAttachments((prev) => [...prev, ...next]);
  }

  async function handleSend() {
    const html = (editorRef.current?.getHtml() ?? bodyHtml).trim();
    if (!to.length) {
      onError?.("Bitte mindestens einen Empfänger angeben.");
      return;
    }
    if (!subject.trim()) {
      onError?.("Bitte einen Betreff angeben.");
      return;
    }
    if (!html) {
      onError?.("Bitte einen Mailtext eingeben.");
      return;
    }

    setSending(true);
    try {
      const attachments = await encodeLocalMailAttachments(localAttachments);
      await sendDokumentMail(docId, {
        to: joinEmails(to),
        cc: cc.length ? joinEmails(cc) : undefined,
        bcc: bcc.length ? joinEmails(bcc) : undefined,
        subject: subject.trim(),
        html_body: html,
        mark_versendet: markVersendet,
        versand_art: vorlage === "angebot_nachfass" ? "angebot_nachfass" : undefined,
        attachments: attachments.length ? attachments : undefined,
        sharepoint_item_ids: selectedSp.size ? [...selectedSp] : undefined,
        attach_dokument_ids: selectedRelatedIds.size ? [...selectedRelatedIds] : undefined,
        attach_timesheet_ids: selectedTimesheetIds.size ? [...selectedTimesheetIds] : undefined,
      });
      await onSent();
      onClose();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : "E-Mail konnte nicht versendet werden.");
    } finally {
      setSending(false);
    }
  }

  const dirty =
    !loading &&
    (bodyEdited ||
      !emailsEqual(to, baseline.to) ||
      !emailsEqual(cc, baseline.cc) ||
      !emailsEqual(bcc, baseline.bcc) ||
      subject !== baseline.subject ||
      !idSetsEqual(selectedRelatedIds, baselineRelatedIds) ||
      !idSetsEqual(selectedTimesheetIds, baselineTimesheetIds) ||
      selectedSp.size > 0 ||
      localAttachments.length > 0);

  const attachmentCount =
    (primaryAttachment ? 1 : 0) +
    selectedTimesheetIds.size +
    selectedRelatedIds.size +
    selectedSp.size +
    localAttachments.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Dokument per E-Mail senden"
      size="4xl"
      zIndexClass={zIndexClass}
      bodyClassName="max-h-[min(78vh,860px)] overflow-x-hidden overflow-y-auto"
      footer={
        <>
          <label className="mr-auto flex items-center gap-2 text-sm text-label-secondary">
            <input
              type="checkbox"
              checked={markVersendet}
              onChange={(e) => setMarkVersendet(e.target.checked)}
              disabled={loading || sending}
            />
            Als „Versendet“ markieren
          </label>
          <DialogCancelButton onClose={onClose} disabled={sending} variant="ghost" />
          <Button type="button" onClick={() => void handleSend()} loading={sending} disabled={loading}>
            Senden
            {attachmentCount > 0
              ? ` (${attachmentCount} ${attachmentCount === 1 ? "Anhang" : "Anhänge"})`
              : ""}
          </Button>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-label-secondary">Mail-Vorschau wird geladen …</p>
      ) : (
        <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <div className="min-w-0 space-y-3">
            <EmailChipInput
              label="Empfänger"
              value={to}
              onChange={setTo}
              suggestions={suggestions}
              placeholder="Name oder E-Mail suchen …"
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <EmailChipInput
                label="CC"
                value={cc}
                onChange={setCc}
                suggestions={suggestions}
                placeholder="optional"
              />
              <EmailChipInput
                label="BCC"
                value={bcc}
                onChange={setBcc}
                suggestions={suggestions}
                placeholder="optional"
              />
            </div>
            <TextInput
              label="Betreff"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <div className="space-y-1">
              <span className="text-sm text-label-secondary">Nachricht</span>
              <HtmlMailBodyEditor
                ref={editorRef}
                value={bodyHtml}
                onChange={(next) => {
                  setBodyHtml(next);
                  setBodyEdited(true);
                }}
                minRows={7}
                aria-label="Nachricht"
              />
            </div>
          </div>

          <div className="min-w-0 space-y-3 lg:max-h-[min(68vh,720px)] lg:overflow-y-auto lg:overflow-x-hidden lg:pl-1 lg:pr-2">
            <section className="rounded-ios border border-separator p-3">
              <p className="mb-2 text-sm font-semibold text-label">Anhänge</p>
              <ul className="space-y-1 text-sm">
                {primaryAttachment ? (
                  <li className="rounded-md bg-fill-tertiary px-2 py-1.5">
                    <span className="block break-all font-medium text-label">
                      {primaryAttachment.filename}
                    </span>
                    <span className="text-xs text-label-tertiary">
                      {primaryAttachment.label} · immer dabei
                    </span>
                  </li>
                ) : null}
              </ul>
              {versandFormat === "xrechnung" &&
              (selectedTimesheetIds.size > 0 ||
                selectedRelatedIds.size > 0 ||
                selectedSp.size > 0 ||
                localAttachments.length > 0) ? (
                <p className="mt-2 rounded-md bg-orange-500/10 px-2 py-1.5 text-xs text-orange-600">
                  Achtung: Viele XRechnung-Empfänger (z. B. DFS) akzeptieren nur E-Mails mit genau
                  einer XML-Datei. Stundennachweise und Lieferscheine sind bereits in der
                  X-Rechnung eingebettet – zusätzliche Mail-Anhänge können zur Ablehnung führen.
                </p>
              ) : null}

              {suggestedTimesheets.length > 0 || suggestedLieferscheine.length > 0 ? (
                <div className="mt-3">
                  <p className="mb-1 text-xs font-medium text-label-secondary">
                    {suggestedTimesheets.length > 0 && suggestedLieferscheine.length > 0
                      ? "Stundennachweise und Lieferscheine zur Rechnung"
                      : suggestedLieferscheine.length > 0
                        ? "Lieferschein zur Rechnung"
                        : "Stundennachweis zur Rechnung"}
                  </p>
                  <ul className="space-y-1">
                    {suggestedTimesheets.map((ts) => {
                      const disabled = ts.has_pdf === false;
                      return (
                        <li key={`ts-${ts.id}`}>
                          <label
                            className={
                              disabled
                                ? "flex cursor-not-allowed gap-2 rounded-md px-2 py-1 opacity-60"
                                : "flex cursor-pointer gap-2 rounded-md px-2 py-1 hover:bg-fill-tertiary"
                            }
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              disabled={disabled}
                              checked={selectedTimesheetIds.has(ts.id)}
                              onChange={(e) => {
                                setSelectedTimesheetIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(ts.id);
                                  else next.delete(ts.id);
                                  return next;
                                });
                              }}
                            />
                            <span className="min-w-0">
                              <span className="break-all text-label">{ts.label}</span>
                              <span className="block truncate text-xs text-label-tertiary">
                                {proofAttachmentHint(ts)}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                    {suggestedLieferscheine.map((ls) => {
                      const disabled = ls.has_pdf === false;
                      return (
                        <li key={`ls-${ls.id}`}>
                          <label
                            className={
                              disabled
                                ? "flex cursor-not-allowed gap-2 rounded-md px-2 py-1 opacity-60"
                                : "flex cursor-pointer gap-2 rounded-md px-2 py-1 hover:bg-fill-tertiary"
                            }
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              disabled={disabled}
                              checked={selectedRelatedIds.has(ls.id)}
                              onChange={(e) => {
                                setSelectedRelatedIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(ls.id);
                                  else next.delete(ls.id);
                                  return next;
                                });
                              }}
                            />
                            <span className="min-w-0">
                              <span className="break-all text-label">{ls.label}</span>
                              <span className="block truncate text-xs text-label-tertiary">
                                {proofAttachmentHint(ls)}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {relatedDocuments.length > 0 ? (
                <div className="mt-3">
                  <p className="mb-1 text-xs font-medium text-label-secondary">
                    Weitere Dokumente {auftragId ? "aus dem Projekt" : "vom Kunden"}
                  </p>
                  <ul className="max-h-36 space-y-1 overflow-y-auto">
                    {relatedDocuments.map((rel) => (
                      <li key={rel.id}>
                        <label className="flex cursor-pointer gap-2 rounded-md px-2 py-1 hover:bg-fill-tertiary">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedRelatedIds.has(rel.id)}
                            onChange={(e) => {
                              setSelectedRelatedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(rel.id);
                                else next.delete(rel.id);
                                return next;
                              });
                            }}
                          />
                          <span className="min-w-0">
                            <span className="break-all text-label">{rel.label}</span>
                            {rel.dokument_typ === "lieferschein" &&
                            typeof rel.has_signed_pdf === "boolean" ? (
                              <span className="block text-xs text-label-tertiary">
                                {lieferscheinMailVariantLabel(rel.has_signed_pdf)}
                              </span>
                            ) : null}
                            {rel.betreff ? (
                              <span className="block truncate text-xs text-label-tertiary">
                                {rel.betreff}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>

            {auftragId ? (
              <section className="rounded-ios border border-separator p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-label">Projektordner</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Neu laden"
                    aria-label="Anhänge neu laden"
                    loading={spLoading}
                    disabled={spLoading}
                    onClick={() => void loadSharepointItems()}
                  >
                    ↻
                  </Button>
                </div>
                <p className="mb-2 text-xs text-label-tertiary">{spHint}</p>
                <SharePointMailAttachmentList
                  items={spItems}
                  selectedIds={selectedSp}
                  listClassName="max-h-36"
                  onToggle={(id, checked) => {
                    setSelectedSp((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(id);
                      else next.delete(id);
                      return next;
                    });
                  }}
                />
              </section>
            ) : null}

            <section className="rounded-ios border border-separator p-3">
              <p className="mb-1 text-sm font-semibold text-label">Eigene Dateien</p>
              <div
                className={`mb-2 rounded-lg border-2 border-dashed px-3 py-4 text-center text-sm transition-colors ${
                  localDropActive
                    ? "border-accent bg-accent/10 text-label"
                    : "border-separator text-label-secondary"
                }`}
                onDragEnter={(e) => {
                  if (!e.dataTransfer?.types?.includes("Files")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  localDropCounterRef.current += 1;
                  setLocalDropActive(true);
                }}
                onDragOver={(e) => {
                  if (!e.dataTransfer?.types?.includes("Files")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = "copy";
                }}
                onDragLeave={(e) => {
                  if (!e.dataTransfer?.types?.includes("Files")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  localDropCounterRef.current = Math.max(0, localDropCounterRef.current - 1);
                  if (localDropCounterRef.current === 0) setLocalDropActive(false);
                }}
                onDrop={(e) => {
                  if (!e.dataTransfer?.files?.length) return;
                  e.preventDefault();
                  e.stopPropagation();
                  localDropCounterRef.current = 0;
                  setLocalDropActive(false);
                  addLocalFiles(e.dataTransfer.files);
                }}
              >
                Dateien hierher ziehen
              </div>
              <input
                ref={localFileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) addLocalFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => localFileInputRef.current?.click()}
              >
                Dateien auswählen …
              </Button>
              {localAttachments.length ? (
                <ul className="mt-2 space-y-1 text-sm">
                  {localAttachments.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-center justify-between gap-2 rounded-md bg-fill-tertiary px-2 py-1"
                    >
                      <span className="min-w-0 break-all">
                        {entry.file.name}
                        {entry.file.size ? ` · ${formatAttachmentSize(entry.file.size)}` : ""}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`${entry.file.name} entfernen`}
                        onClick={() =>
                          setLocalAttachments((prev) => prev.filter((x) => x.id !== entry.id))
                        }
                      >
                        ×
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          </div>
        </div>
      )}
    </Dialog>
  );
}
